import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from "react";
import { Spinner } from "./Spinner.js";

/**
 * B-1: warianty przycisku. Hero (B-6) ma osobna klase i nie jest wariantem tego
 * komponentu - to jedyna akcja z wypelnieniem marki i wystepuje raz na widok.
 *
 * DG-1: dwie intensywnosci przycisku niszczacego. "danger" to wersja soft (tint),
 * czyli wyzwalacz w tresci; "danger-solid" jest zarezerwowany dla finalnego
 * potwierdzenia w D-4 i poza ConfirmDialog nie powinien sie pojawiac.
 */
export type WariantPrzycisku = "primary" | "secondary" | "danger" | "danger-solid";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled" | "className"> {
  wariant?: WariantPrzycisku;
  /** B-10: kwadratowy przycisk tylko z ikona. Wymaga aria-label (IC-3). */
  tylkoIkona?: boolean;
  /** B-5: trwa akcja. Pokazuje spinner i blokuje drugie klikniecie. */
  zajety?: boolean;
  /** S-10: akcja niedostepna. Powod wyjasnij tooltipem (TT-5), nie samym wygaszeniem. */
  wylaczony?: boolean;
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * B-5: komplet stanow, w tym blokada drugiego klikniecia.
 *
 * KLUCZOWE: stan zajetosci NIE ustawia natywnego `disabled`. Ustawienie `disabled`
 * na przycisku, ktory wlasnie ma fokus, przenosi fokus na <body> - uzytkownik
 * klawiatury traci miejsce w dokumencie i laduje na poczatku strony (lamie A-4).
 * Zamiast tego: aria-disabled + aria-busy dla czytnika, wygaszenie przez CSS
 * ([aria-disabled="true"] w controls.css) i straznik w handlerze.
 */
export function Button({
  wariant = "secondary",
  tylkoIkona = false,
  zajety = false,
  wylaczony = false,
  children,
  onClick,
  type = "button",
  ref,
  ...reszta
}: Props): React.JSX.Element {
  const nieaktywny = zajety || wylaczony;

  function obsluzKlik(e: MouseEvent<HTMLButtonElement>): void {
    if (nieaktywny) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  }

  return (
    <button
      {...reszta}
      ref={ref}
      type={type}
      className={`btn btn--${wariant}${tylkoIkona ? " btn--ikona" : ""}`}
      aria-disabled={nieaktywny || undefined}
      aria-busy={zajety || undefined}
      onClick={obsluzKlik}
    >
      {zajety && !tylkoIkona && <Spinner rozmiar="s" />}
      {children}
    </button>
  );
}
